import { applyBufferDayAdjustment } from '../dde/bufferDays'
import type { Indication } from '../types'
import type {
  ConfigurationScopedOverride,
  StandardRevision,
  ConfigurationMaterialization,
} from './types'
import { applyConfigurationOperation } from './operations'

/** Standard and legacy intervals are authored values; override v2 values are enforced. */
export const enforcedIndication = (
  indication: Indication,
  bufferIntervals = true,
): Indication => {
  const frequency = applyBufferDayAdjustment(
    indication.frequencyMaxFrequency,
    indication.frequencyTimePeriod,
    bufferIntervals,
  )
  const units = applyBufferDayAdjustment(
    indication.maxUnitsOverHcpcsMaxFrequency,
    indication.maxUnitsOverHcpcsTimePeriod,
    bufferIntervals,
  )
  return {
    ...indication,
    ...(frequency.adjustedDays !== undefined && {
      frequencyMaxFrequency: frequency.adjustedDays,
      frequencyTimePeriod: 'day',
    }),
    ...(units.adjustedDays !== undefined && {
      maxUnitsOverHcpcsMaxFrequency: units.adjustedDays,
      maxUnitsOverHcpcsTimePeriod: 'day',
    }),
  }
}

export const enforcedStandard = (
  standard: StandardRevision,
): StandardRevision => ({
  ...standard,
  indications: standard.indications.map((indication) =>
    enforcedIndication(indication),
  ),
})

export const hasBufferedOverrides = (
  overrides: ConfigurationScopedOverride[],
): boolean =>
  overrides.some(
    (override) =>
      !!override.addedIndication?.frequencyMaxFrequency ||
      !!override.addedIndication?.maxUnitsOverHcpcsMaxFrequency ||
      override.operations.some(
        (operation) =>
          (operation.kind === 'set_frequency_rule' &&
            ('frequencyMaxFrequency' in operation.value ||
              'frequencyTimePeriod' in operation.value)) ||
          (operation.kind === 'set_max_units_over_time_rule' &&
            ('maxUnitsOverHcpcsMaxFrequency' in operation.value ||
              'maxUnitsOverHcpcsTimePeriod' in operation.value)),
      ),
  )

export const enforcedMaterialization = (
  materialization: ConfigurationMaterialization,
  standard: StandardRevision,
): ConfigurationMaterialization => {
  const before = new Map(
    standard.indications.map(
      (indication) => [indication.id, enforcedIndication(indication)] as const,
    ),
  )
  const after = new Map(
    materialization.effectiveIndications.map(
      (indication) => [indication.id, enforcedIndication(indication)] as const,
    ),
  )
  /* Historical emitted rows take precedence over recomputed changed periods. */
  for (const impact of materialization.ddeImpacts) {
    const indication = after.get(impact.indicationId)
    if (!indication || !impact.after) continue
    if (
      impact.policyType === 'Frequency' &&
      impact.after.freq_timeperiod !== undefined
    ) {
      indication.frequencyMaxFrequency = impact.after.freq_timeperiod
      indication.frequencyTimePeriod = impact.after.freq_timeperioduom
    }
    if (
      impact.policyType === 'MaxUnitsOverTime' &&
      impact.after.unit_timeperiod !== undefined
    ) {
      indication.maxUnitsOverHcpcsMaxFrequency = impact.after.unit_timeperiod
      indication.maxUnitsOverHcpcsTimePeriod = impact.after.unit_timeperioduom
    }
  }
  return {
    ...materialization,
    effectiveIndications: [...after.values()],
    impacts: materialization.impacts
      .map((impact) => ({
        ...impact,
        before:
          before.get(impact.indicationId)?.[impact.field as keyof Indication] ??
          impact.before,
        after:
          after.get(impact.indicationId)?.[impact.field as keyof Indication] ??
          impact.after,
      }))
      .filter(
        (impact) =>
          JSON.stringify(impact.before ?? null) !==
          JSON.stringify(impact.after ?? null),
      ),
  }
}

export const literalOverrides = (
  overrides: ConfigurationScopedOverride[],
  standard: Pick<StandardRevision, 'indications'>,
): ConfigurationScopedOverride[] =>
  overrides.map((override) => ({
    ...override,
    ...(override.addedIndication && {
      addedIndication: enforcedIndication(override.addedIndication),
    }),
    operations: override.operations.map((operation) => {
      if (
        operation.kind !== 'set_frequency_rule' &&
        operation.kind !== 'set_max_units_over_time_rule'
      )
        return operation
      const baseline = standard.indications.find(
        ({ id }) => id === operation.indicationId,
      )
      if (!baseline) return operation
      const effective = enforcedIndication(
        applyConfigurationOperation(baseline, operation),
      )
      if (operation.kind === 'set_frequency_rule') {
        const changesInterval =
          'frequencyMaxFrequency' in operation.value ||
          'frequencyTimePeriod' in operation.value
        return {
          ...operation,
          value: {
            ...operation.value,
            ...(changesInterval && {
              frequencyMaxFrequency: effective.frequencyMaxFrequency,
              frequencyTimePeriod: effective.frequencyTimePeriod,
            }),
          },
        }
      }
      const changesInterval =
        'maxUnitsOverHcpcsMaxFrequency' in operation.value ||
        'maxUnitsOverHcpcsTimePeriod' in operation.value
      return {
        ...operation,
        value: {
          ...operation.value,
          ...(changesInterval && {
            maxUnitsOverHcpcsMaxFrequency:
              effective.maxUnitsOverHcpcsMaxFrequency,
            maxUnitsOverHcpcsTimePeriod: effective.maxUnitsOverHcpcsTimePeriod,
          }),
        },
      }
    }),
  }))
